import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Sparkles, Wrench, Bug, Bell as BellIcon, Megaphone, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Top-nav "What's new" bell chip.
// Shows a red unread pip when there are announcements the crew hasn't
// tapped Got It on yet. Clicking opens a lightweight popover that
// combines UNREAD (top, highlighted) + a scrollable HISTORY of past
// notes the user was ever targeted by — so nobody misses a release
// note they dismissed too quickly.

const CATEGORY_META = {
  new_feature:  { icon: Sparkles, label: "New feature",  tone: "text-accent" },
  improvement:  { icon: Wrench,   label: "Improvement",  tone: "text-emerald-600 dark:text-emerald-400" },
  bug_fix:      { icon: Bug,      label: "Bug fix",      tone: "text-amber-600 dark:text-amber-400" },
  announcement: { icon: BellIcon, label: "Announcement", tone: "text-sky-600 dark:text-sky-400" },
};

export default function WhatsNewBell() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/whats-new/my", { params: { include_dismissed: true, limit: 30 } });
      setRows(data || []);
    } catch { /* not signed in / offline — hide */ setRows([]); }
  };

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const onLive = (e) => { if (e?.detail?.type === "whats_new.published") load(); };
    window.addEventListener("yspa:ws-broadcast", onLive);
    // Light 5 min poll — this widget is passive, so slow cadence is fine.
    const t = setInterval(load, 5 * 60 * 1000);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("yspa:ws-broadcast", onLive);
      clearInterval(t);
    };
  }, []);

  const { unread, history } = useMemo(() => {
    const u = rows.filter((r) => !r.dismissed);
    const h = rows.filter((r) => r.dismissed);
    return { unread: u, history: h };
  }, [rows]);

  const dismiss = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/whats-new/${r.id}/dismiss`);
      await load();
    } catch { /* silent */ }
    finally { setBusyId(null); }
  };

  if (rows.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative p-2 rounded-full hover:bg-muted transition-colors"
          aria-label={unread.length > 0 ? `${unread.length} new announcement${unread.length === 1 ? "" : "s"}` : "What's new"}
          data-testid="whatsnew-bell"
        >
          <Bell className="h-5 w-5 text-primary/80" strokeWidth={1.6} />
          {unread.length > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold flex items-center justify-center animate-pulse"
              data-testid="whatsnew-bell-badge"
            >
              {unread.length > 9 ? "9+" : unread.length}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="whatsnew-bell-popover">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-accent" strokeWidth={1.5} />
          <span className="font-display text-base">What&apos;s new</span>
          {unread.length > 0 && (
            <Badge variant="outline" className="ml-auto text-[10px] border-accent/40 text-accent">
              {unread.length} unread
            </Badge>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {unread.length === 0 && history.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No announcements yet.
            </div>
          )}
          {unread.length > 0 && (
            <div className="p-2 space-y-1.5">
              {unread.map((r) => (
                <Row key={r.id} row={r} unread onDismiss={() => dismiss(r)} busy={busyId === r.id} />
              ))}
            </div>
          )}
          {history.length > 0 && (
            <div className="p-2 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1.5">Read earlier</div>
              <div className="space-y-1.5">
                {history.map((r) => (<Row key={r.id} row={r} />))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ row, unread = false, onDismiss, busy }) {
  const cats = (row.categories || []).map((c) => CATEGORY_META[c]).filter(Boolean);
  return (
    <div
      className={`p-3 border rounded-md ${unread ? "border-accent/40 bg-accent/[0.04]" : "border-border/70"}`}
      data-testid={`whatsnew-bell-row-${row.id}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`font-medium text-sm ${unread ? "text-primary" : "text-foreground/80"}`}>{row.title}</span>
            {cats.map((c, i) => {
              const Icon = c.icon;
              return (
                <span key={i} className={`inline-flex items-center gap-0.5 text-[10px] ${c.tone}`} title={c.label}>
                  <Icon className="h-2.5 w-2.5" />
                </span>
              );
            })}
          </div>
          {row.body && (
            <p className={`text-[12px] mt-1 whitespace-pre-wrap leading-snug ${unread ? "text-foreground/80" : "text-muted-foreground"}`}>{row.body}</p>
          )}
          {row.created_at && (
            <div className="text-[10px] text-muted-foreground/80 mt-1.5">
              {new Date(row.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              {row.created_by_name && ` · ${row.created_by_name}`}
            </div>
          )}
        </div>
        {unread && onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="text-[10px] text-accent hover:underline whitespace-nowrap inline-flex items-center gap-1"
            data-testid={`whatsnew-bell-dismiss-${row.id}`}
          >
            <Check className="h-3 w-3" /> Got it
          </button>
        )}
      </div>
    </div>
  );
}
